// A minimal floating toast stack, bottom-right — used for share
// notifications (notifications.ts) and any fire-and-forget "something
// happened" message. Plain DOM, no framework, same inline-style/
// document.body-append instinct as contextmenu.ts (the closest existing
// floating-UI precedent in this codebase); there's no shared "floating
// layer" abstraction to reuse beyond that instinct, so this is its own small
// module rather than bolted onto contextmenu.ts's single-menu-at-a-time
// model.
//
// Position history: top-centre covered the map's search box (re-shoot
// finding 8); top-right then covered the right panel's "Clear" button and,
// on project pages, the map's "Visibility" button (take-2 notes,
// 2026-10-08). It now stacks bottom-right, newest at the bottom. The base
// offset clears the map's attribution strip, and placeStack lifts the stack
// above anything else pinned to the viewport foot that it would overlap
// horizontally: the field app's tab bar and AR buttons, the phone layout's
// "zoom in" prompt, and the map legend (bottom-left, so this only bites at
// phone width, where the stack is nearly full-width).
const TOAST_AUTO_DISMISS_MS = 8000;
const TOAST_GAP_PX = 8;
const TOAST_BASE_BOTTOM_PX = 32; // clears the maplibre attribution strip

// Elements pinned to the bottom of the viewport that the stack must sit above.
const BOTTOM_OBSTACLES = [
  ".field-app:not([hidden]) .fa-nav",
  ".field-app:not([hidden]) .fa-ar-bottom",
  ".mobile-zoom-prompt",
  ".map-legend",
];
const TOAST_MAX_WIDTH_PX = 448; // 28rem

let stackEl: HTMLDivElement | null = null;

function isShowing(el: Element): boolean {
  const r = el.getBoundingClientRect();
  if (r.width === 0 || r.height === 0) return false;
  const cs = getComputedStyle(el);
  return cs.display !== "none" && cs.visibility !== "hidden";
}

/** Bottom-right, lifted above any visible bottom-pinned chrome. */
function placeStack(stack: HTMLDivElement): void {
  let bottom = TOAST_BASE_BOTTOM_PX;
  const vh = window.innerHeight;
  // the stack's widest possible left edge; obstacles left of it don't matter
  const stackLeft = window.innerWidth - 12 - Math.min(TOAST_MAX_WIDTH_PX, window.innerWidth - 24);
  for (const sel of BOTTOM_OBSTACLES) {
    document.querySelectorAll(sel).forEach((el) => {
      if (!isShowing(el) || el.getBoundingClientRect().right <= stackLeft) return;
      const top = el.getBoundingClientRect().top;
      if (top < vh) bottom = Math.max(bottom, Math.round(vh - top) + TOAST_GAP_PX);
    });
  }
  stack.style.bottom = `${bottom}px`;
}

function ensureStack(): HTMLDivElement {
  if (stackEl) {
    placeStack(stackEl);
    return stackEl;
  }
  const stack = document.createElement("div");
  stack.id = "toast-stack";
  stack.className = "toast-stack";
  stack.style.position = "fixed";
  stack.style.right = "12px";
  stack.style.left = "auto";
  stack.style.top = "auto";
  stack.style.maxWidth = "min(28rem, calc(100vw - 24px))";
  stack.style.zIndex = "2000"; // above contextmenu.ts's 1000
  stack.style.display = "flex";
  stack.style.flexDirection = "column";
  stack.style.alignItems = "flex-end";
  stack.style.gap = "0.4rem";
  stack.style.pointerEvents = "none"; // the stack itself must not block map clicks
  placeStack(stack);
  document.body.append(stack);
  window.addEventListener("resize", () => placeStack(stack));
  stackEl = stack;
  return stack;
}

/** Shows a dismissible toast bottom-right: its close button (or a click
 * anywhere on it) dismisses it early, otherwise it is auto-removed after
 * TOAST_AUTO_DISMISS_MS. A dismissed toast is gone for good: nothing
 * re-shows it (the notification poll only toasts ids newer than the last
 * one it saw, and the share poll consumes what it shows). */
export function showToast(message: string): void {
  const stack = ensureStack();
  const toast = document.createElement("div");
  toast.className = "plain-toast";
  toast.setAttribute("role", "status");
  toast.style.pointerEvents = "auto";
  toast.style.display = "flex";
  toast.style.alignItems = "flex-start";
  toast.style.gap = "0.5rem";
  toast.style.background = "#1a1a1a";
  toast.style.color = "#fff";
  toast.style.padding = "0.6rem 0.5rem 0.6rem 1rem";
  toast.style.borderRadius = "6px";
  toast.style.boxShadow = "0 2px 10px rgba(0,0,0,0.35)";
  toast.style.fontSize = "0.85rem";
  toast.style.maxWidth = "28rem";
  toast.style.textAlign = "left";
  toast.style.cursor = "pointer";
  const text = document.createElement("span");
  text.style.flex = "1";
  text.style.minWidth = "0";
  text.textContent = message;
  toast.append(text, closeButton());

  let dismissTimer: number | null = null;
  const dismiss = () => {
    if (dismissTimer !== null) window.clearTimeout(dismissTimer);
    toast.remove();
  };
  toast.addEventListener("click", (e) => {
    e.stopPropagation();
    dismiss();
  });
  dismissTimer = window.setTimeout(dismiss, TOAST_AUTO_DISMISS_MS);

  stack.append(toast);
}

/** The ✕ every toast carries (styles/flowStageA.css .notif-toast-close: a
 * 28 px target, 40 px on touch screens so it is easy to hit on a phone). */
function closeButton(): HTMLButtonElement {
  const close = document.createElement("button");
  close.type = "button";
  close.className = "notif-toast-close";
  close.setAttribute("aria-label", "Dismiss");
  close.title = "Dismiss";
  close.textContent = "✕";
  return close;
}

export interface ActionToastOptions {
  title: string;
  body?: string | null;
  meta?: string | null;
  // Called when the toast itself is clicked (not the close button); the
  // toast is then dismissed.
  onOpen?: () => void;
  autoDismissMs?: number;
  // Buttons under the text (MSQA "Go to signatures" / "Undo", row #83); each
  // dismisses the toast after running.
  actions?: { label: string; onClick: () => void }[];
}

/** A richer toast for live notifications (notificationsInbox.ts): title,
 * optional body/meta line, a close button, and a click-through. Shares the
 * bottom-right stack with showToast, so several arrivals stack. */
export function showActionToast(opts: ActionToastOptions): HTMLDivElement {
  const stack = ensureStack();
  const toast = document.createElement("div");
  toast.className = "notif-toast";
  toast.setAttribute("role", "status");
  const text = document.createElement("div");
  text.className = "notif-toast-text";
  const title = document.createElement("div");
  title.className = "notif-toast-title";
  title.textContent = opts.title;
  text.append(title);
  if (opts.body) {
    const body = document.createElement("div");
    body.className = "notif-toast-body";
    body.textContent = opts.body.length > 160 ? `${opts.body.slice(0, 157)}…` : opts.body;
    text.append(body);
  }
  if (opts.meta) {
    const meta = document.createElement("div");
    meta.className = "notif-toast-meta";
    meta.textContent = opts.meta;
    text.append(meta);
  }
  const close = closeButton();
  toast.append(text, close);

  let dismissTimer: number | null = null;
  const dismiss = () => {
    if (dismissTimer !== null) window.clearTimeout(dismissTimer);
    toast.remove();
  };
  if (opts.actions?.length) {
    const row = document.createElement("div");
    row.className = "notif-toast-actions";
    for (const a of opts.actions) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "notif-toast-action";
      b.textContent = a.label;
      b.addEventListener("click", (e) => {
        e.stopPropagation();
        dismiss();
        a.onClick();
      });
      row.append(b);
    }
    text.append(row);
  }
  close.addEventListener("click", (e) => {
    e.stopPropagation();
    dismiss();
  });
  toast.addEventListener("click", (e) => {
    e.stopPropagation(); // else the document click that closes popovers undoes onOpen
    dismiss();
    opts.onOpen?.();
  });
  // Pause auto-dismiss while hovered, so a toast being read doesn't vanish.
  const arm = () => {
    if (dismissTimer !== null) window.clearTimeout(dismissTimer);
    dismissTimer = window.setTimeout(dismiss, opts.autoDismissMs ?? 15_000);
  };
  toast.addEventListener("mouseenter", () => {
    if (dismissTimer !== null) window.clearTimeout(dismissTimer);
    dismissTimer = null;
  });
  toast.addEventListener("mouseleave", arm);
  arm();
  stack.append(toast);
  return toast;
}
